import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import SlideOver from '../components/SlideOver';
import CreateChangeOrderModal from '../components/CreateChangeOrderModal';
import { CO_STATUSES, CO_CLIENT_VISIBLE_STATUSES, type CoStatus, type CoType, type CoCostImpact } from '@blueprint/shared';

// ── Types ────────────────────────────────────────────────────────────────────

interface COContent {
  coNumber: string;
  title: string;
  description: string;
  type: CoType;
  sourceRef: string | null;
  impact: { storyPoints?: number; euSize?: string; reviewUnits?: number; estimatedDays?: number; costImpact: CoCostImpact; costNote?: string | null };
  options: { id: string; label: string; description: string; recommended: boolean }[];
  status: CoStatus;
  submittedAt: string | null;
  respondedAt: string | null;
  clientResponse: string | null;
  selectedOption: string | null;
  autoGenerated?: boolean;
}

interface ChangeOrder {
  id: string;
  title: string;
  createdAt: string;
  sourceRef: string | null;
  autoGenerated: boolean;
  content: COContent;
}

// ── Constants ────────────────────────────────────────────────────────────────

const STATUS_TABS = ['all', ...CO_STATUSES] as const;
type StatusTab = (typeof STATUS_TABS)[number];
// #3710 — a caller who may not see drafts gets only the tabs of the statuses the API will ever list for
// them (DRAFT / DISMISSED / RESOLVED are never returned to a client). UX only: the API is the control.
const CLIENT_STATUS_TABS: readonly StatusTab[] = ['all', ...CO_CLIENT_VISIBLE_STATUSES];

const STATUS_BADGE: Record<CoStatus, React.CSSProperties> = {
  DRAFT:     { background: 'var(--surface-alt)', color: 'var(--text-secondary)', border: '1px solid var(--border)' },
  SUBMITTED: { background: 'rgba(245,158,11,0.12)', color: 'var(--warning)', border: '1px solid rgba(245,158,11,0.3)' },
  APPROVED:  { background: 'rgba(16,185,129,0.12)', color: 'var(--success)', border: '1px solid rgba(16,185,129,0.3)' },
  REJECTED:  { background: 'rgba(239,68,68,0.10)', color: 'var(--error)', border: '1px solid rgba(239,68,68,0.25)' },
  DEFERRED:  { background: 'rgba(124,58,237,0.10)', color: 'var(--primary)', border: '1px solid rgba(124,58,237,0.25)' },
  DISMISSED: { background: 'var(--surface-alt)', color: 'var(--text-secondary)', border: '1px solid var(--border)' },
  RESOLVED:  { background: 'rgba(16,185,129,0.12)', color: 'var(--success)', border: '1px solid rgba(16,185,129,0.3)' },
};

const TYPE_LABEL: Record<CoType, string> = {
  SPIKE: 'Spike',
  COMPLEXITY_INCREASE: 'Complexity Increase',
  REQUIREMENTS_GAP: 'Requirements Gap',
  MANUAL: 'Manual',
  SECURITY_FINDING: 'Security finding',
  INCIDENT: 'Incident',
  DEFECT: 'Defect',
};

const COST_LABEL: Record<CoCostImpact, string> = {
  NONE: 'No cost impact',
  INCLUDED: 'Included in budget',
  ADDITIONAL: 'Additional charge',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

// Stored values are not trusted to be enum members: a plain `TABLE[key]` resolves '__proto__',
// 'constructor' and 'toString' to Object.prototype members (#3312 R3). Own keys only.
function own<V>(table: Record<string, V>, key: unknown): V | undefined {
  return typeof key === 'string' && Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined;
}

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function btnStyle(color: string, ghost = false): React.CSSProperties {
  return {
    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500,
    padding: '4px 12px', background: 'transparent',
    border: `1px solid ${ghost ? 'var(--border)' : color}`,
    borderRadius: 'var(--radius-sm)', color: ghost ? 'var(--text-secondary)' : color,
    cursor: 'pointer',
  };
}

// ── Detail Modal ─────────────────────────────────────────────────────────────

interface DetailModalProps {
  co: ChangeOrder;
  onClose: () => void;
  onSubmit: (id: string) => Promise<void>;
  submitting: boolean;
  canSeeDrafts: boolean;
}

function CODetailModal({ co, onClose, onSubmit, submitting, canSeeDrafts }: DetailModalProps) {
  const c = co.content;
  const sectionLabel: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
    color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: 6, marginTop: 20,
  };

  return (
    <SlideOver
      isOpen
      onClose={onClose}
      title="Change Order Details"
      subtitle={`${c.coNumber} — ${c.title}`}
      width="lg"
      footer={
        <div style={{ display: 'flex', gap: 8 }}>
          {canSeeDrafts && c.status === 'DRAFT' && (
            <button onClick={() => void onSubmit(co.id)} disabled={submitting} style={btnStyle('var(--primary)')}>
              {submitting ? 'Submitting…' : 'Submit to Client'}
            </button>
          )}
          <button onClick={onClose} style={btnStyle('var(--text-secondary)', true)}>Close</button>
        </div>
      }
    >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 9999, ...own(STATUS_BADGE, c.status) }}>
            {c.status}
          </span>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', padding: '2px 8px', borderRadius: 9999, background: 'var(--surface-alt)' }}>
            {own(TYPE_LABEL, c.type) ?? c.type}
          </span>
        </div>

        {/* Description */}
        <p style={sectionLabel}>Description</p>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{c.description}</p>

        {/* Impact */}
        <p style={sectionLabel}>Impact</p>
        <div style={{ display: 'flex', gap: 16, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>
          {c.impact.storyPoints != null && <span>{c.impact.storyPoints} story points</span>}
          {c.impact.euSize && <span>EU: {c.impact.euSize}</span>}
          {c.impact.reviewUnits != null && <span>RU: {c.impact.reviewUnits}</span>}
          {/* #3605 PR-1 (AVR 6302006616) — days only when present; never " days". */}
          {typeof c.impact.estimatedDays === 'number' && <span>{c.impact.estimatedDays} days</span>}
          <span>{own(COST_LABEL, c.impact.costImpact) ?? c.impact.costImpact}</span>
        </div>

        {/* Source ref */}
        {c.sourceRef && (
          <>
            <p style={sectionLabel}>Source Reference</p>
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)' }}>{c.sourceRef}</p>
          </>
        )}

        {/* Options */}
        <p style={sectionLabel}>Options for Client</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {c.options.map(o => (
            <div
              key={o.id}
              style={{
                padding: '10px 12px', background: c.selectedOption === o.id ? 'rgba(16,185,129,0.08)' : 'var(--surface-alt)',
                border: `1px solid ${c.selectedOption === o.id ? 'var(--success)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{o.label}</span>
                {o.recommended && <span style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 600 }}>Recommended</span>}
                {c.selectedOption === o.id && <span style={{ fontSize: 11, color: 'var(--success)', fontWeight: 600 }}>Selected by client</span>}
              </div>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>{o.description}</p>
            </div>
          ))}
        </div>

        {/* Client response */}
        {c.clientResponse && (
          <>
            <p style={sectionLabel}>Client Response</p>
            <div style={{ padding: '10px 12px', background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>{c.clientResponse}</p>
            </div>
          </>
        )}

        {/* History */}
        <p style={sectionLabel}>History</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--primary)', flexShrink: 0 }}>●</span>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>Created — {formatDate(co.createdAt)}</span>
          </div>
          {c.submittedAt && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--warning)', flexShrink: 0 }}>●</span>
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>Submitted to client — {formatDate(c.submittedAt)}</span>
            </div>
          )}
          {c.respondedAt && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: c.status === 'APPROVED' ? 'var(--success)' : c.status === 'REJECTED' ? 'var(--error)' : 'var(--primary)', flexShrink: 0 }}>●</span>
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                Client {c.status.toLowerCase()} — {formatDate(c.respondedAt)}
              </span>
            </div>
          )}
        </div>
    </SlideOver>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function ChangeOrdersPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeTab = (searchParams.get('status') ?? 'all') as StatusTab;
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectName, setProjectName] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailCO, setDetailCO] = useState<ChangeOrder | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // #3710 — fail closed: only an explicit `canSeeDrafts: true` from the API shows the draft tabs and Submit.
  const [canSeeDrafts, setCanSeeDrafts] = useState(false);
  const tabs = canSeeDrafts ? STATUS_TABS : CLIENT_STATUS_TABS;

  const fetchCOs = () => {
    if (!projectId) return;
    setLoading(true);
    const url = activeTab === 'all'
      ? `/projects/${projectId}/change-orders`
      : `/projects/${projectId}/change-orders?status=${activeTab}`;
    apiFetch<{ changeOrders: ChangeOrder[]; canSeeDrafts?: boolean }>(url)
      .then(r => { setChangeOrders(r.changeOrders); setCanSeeDrafts(r.canSeeDrafts === true); })
      .catch(() => { setChangeOrders([]); setCanSeeDrafts(false); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!projectId) return;
    apiFetch<{ name: string }>(`/projects/${projectId}`).then(p => setProjectName(p.name)).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    fetchCOs();
  }, [projectId, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitToClient = async (coId: string) => {
    setSubmitting(true);
    try {
      await apiFetch(`/projects/${projectId}/change-orders/${coId}/submit`, { method: 'POST' });
      setDetailCO(null);
      fetchCOs();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to submit change order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        {/* Header */}
        <div style={{ padding: '20px 28px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <button
              onClick={() => navigate(`/projects/${projectId}`)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', padding: 0 }}
            >
              ← {projectName || 'Project'}
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Change Orders</h1>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowCreate(true)}
                style={{
                  fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, padding: '7px 14px',
                  color: 'var(--primary)', background: 'transparent', border: '1px solid var(--primary)', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                }}
              >
                + Quick Change Order
              </button>
              <button
                onClick={() => navigate(`/projects/${projectId}/chat?agentType=change_order`)}
                style={{
                  fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, padding: '7px 14px',
                  color: 'white', background: 'var(--primary)', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                }}
              >
                + AI Change Order Agent
              </button>
            </div>
          </div>

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: 2, marginTop: 16, borderBottom: '1px solid var(--border)' }}>
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setSearchParams(tab === 'all' ? {} : { status: tab })}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
                  padding: '7px 14px', background: 'none', border: 'none', cursor: 'pointer',
                  color: activeTab === tab ? 'var(--primary)' : 'var(--text-secondary)',
                  borderBottom: `2px solid ${activeTab === tab ? 'var(--primary)' : 'transparent'}`,
                  marginBottom: -1,
                }}
              >
                {tab === 'all' ? 'All' : tab.charAt(0) + tab.slice(1).toLowerCase()}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px 28px' }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse" style={{ height: 110, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }} />
              ))}
            </div>
          ) : changeOrders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text-secondary)' }}>
                No {activeTab === 'all' ? '' : activeTab.toLowerCase() + ' '}change orders found
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
                <button onClick={() => setShowCreate(true)} style={{ ...btnStyle('var(--primary)') }}>
                  + Quick Change Order
                </button>
                <button onClick={() => navigate(`/projects/${projectId}/chat?agentType=change_order`)} style={{ ...btnStyle('var(--warning)') }}>
                  + AI Change Order Agent
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 760 }}>
              {changeOrders.map(co => {
                const c = co.content;
                return (
                  <div
                    key={co.id}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px 18px' }}
                  >
                    {/* Card header */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                      <div>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-secondary)' }}>{c.coNumber}</span>
                        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3, marginTop: 2 }}>
                          {c.title}
                        </h3>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 9999, whiteSpace: 'nowrap', ...own(STATUS_BADGE, c.status) }}>
                          {c.status}
                        </span>
                        <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, padding: '2px 8px', borderRadius: 9999, background: 'var(--surface-alt)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                          {own(TYPE_LABEL, c.type) ?? c.type}
                        </span>
                      </div>
                    </div>

                    {/* Description preview */}
                    <p style={{
                      fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      lineHeight: 1.5, marginBottom: 6,
                    }}>
                      {c.description}
                    </p>

                    {/* Impact summary */}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                      {c.impact.storyPoints != null && <span>{c.impact.storyPoints} pts</span>}
                      {c.impact.euSize && <span>EU: {c.impact.euSize}</span>}
                      {c.impact.reviewUnits != null && <span>RU: {c.impact.reviewUnits}</span>}
                      {typeof c.impact.estimatedDays === 'number' && <span>{c.impact.estimatedDays} days</span>}
                      <span>{own(COST_LABEL, c.impact.costImpact) ?? c.impact.costImpact}</span>
                      {co.sourceRef && <span>Ref: {co.sourceRef}</span>}
                    </div>

                    {/* Meta */}
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                      Created {relativeTime(co.createdAt)}
                      {c.submittedAt && ` · Submitted ${relativeTime(c.submittedAt)}`}
                      {c.respondedAt && ` · Responded ${relativeTime(c.respondedAt)}`}
                    </p>

                    {/* Client response preview */}
                    {c.clientResponse && (
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10, fontStyle: 'italic' }}>
                        Client: &ldquo;{c.clientResponse}&rdquo;
                      </p>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button onClick={() => setDetailCO(co)} style={btnStyle('var(--primary)')}>
                        View Details
                      </button>
                      {canSeeDrafts && c.status === 'DRAFT' && (
                        <button
                          onClick={() => void handleSubmitToClient(co.id)}
                          disabled={submitting}
                          style={btnStyle('var(--warning)')}
                        >
                          {submitting ? 'Submitting…' : 'Submit to Client'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      {/* Modals */}
      {detailCO && (
        <CODetailModal
          co={detailCO}
          onClose={() => setDetailCO(null)}
          onSubmit={handleSubmitToClient}
          submitting={submitting}
          canSeeDrafts={canSeeDrafts}
        />
      )}
      {showCreate && projectId && (
        <CreateChangeOrderModal
          projectId={projectId}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchCOs(); }}
        />
      )}
    </div>
  );
}
